import { ArrowRight, Info, type LucideIcon } from "lucide-react";
import { Button } from "@bookdental/ui";
import type { OrganizationType } from "@bookdental/schemas";
import { getVisibleOrgTypeOptions } from "./constants";
import { OrganizationTypeGrid } from "@/components/organization/OrganizationTypeGrid";
import { SeletorDeEspecialidades } from "@/components/services/SeletorDeEspecialidades";

// Onboarding sempre cria a organização PESSOAL — rótulo de DENTAL_OFFICE reflete a identidade
// profissional ("Cirurgião Dentista"), não o nome de uma organização de equipe.
const visibleTypeOptions = getVisibleOrgTypeOptions(true);

type Props = {
  selected: OrganizationType[];
  onToggle: (type: OrganizationType) => void;
  specialties: { id: string; label: string; destaque: boolean }[];
  selectedSpecialtyIds: string[];
  onSpecialtiesChange: (ids: string[]) => void;
  onNext: () => void;
  /** Ação secundária do rodapé — "Sair" (gate obrigatório) ou "Cancelar" (aberto por escolha,
   *  ex: Meu perfil), decidido por quem monta o wizard (ver OnboardingWizard). */
  secondaryAction: { label: string; icon: LucideIcon; onClick: () => void };
};

export function OrgTypeStep({
  selected,
  onToggle,
  specialties,
  selectedSpecialtyIds,
  onSpecialtiesChange,
  onNext,
  secondaryAction,
}: Props) {
  return (
    <>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Escolha a área que melhor descreve você.
      </p>

      <OrganizationTypeGrid options={visibleTypeOptions} selected={selected} onToggle={onToggle} />

      <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5 text-[12.5px] text-brand-800">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          Essa é a sua organização <strong>pessoal</strong> — o seu ponto de partida na plataforma.
          Criar outras organizações depois (uma clínica, um laboratório, uma parceria) não substitui
          nem duplica isso aqui, só te dá mais um espaço de trabalho.
        </p>
      </div>

      {selected.includes("DENTAL_OFFICE") && specialties.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Especialidade — já cria os serviços e procedimentos típicos pra você
          </p>
          {/* O mesmo seletor de Meu perfil: as principais à vista, o resto em "Mostrar outras". */}
          <SeletorDeEspecialidades
            principais={specialties.filter((e) => e.destaque)}
            outras={specialties.filter((e) => !e.destaque)}
            marcadas={selectedSpecialtyIds}
            onChange={onSpecialtiesChange}
            rotuloRecolher="Mostrar só as principais"
            comIcones
          />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={secondaryAction.onClick}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 transition-colors hover:text-zinc-700"
        >
          <secondaryAction.icon className="h-4 w-4" />
          {secondaryAction.label}
        </button>
        <Button disabled={selected.length === 0} onClick={onNext}>
          Continuar
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}
